<template>
  <el-dialog
    :title="isEdit ? '编辑活动' : '新增活动'"
    :visible.sync="innerVisible"
    width="780px"
    top="6vh"
    class="activity-form-dialog"
    :close-on-click-modal="false"
    append-to-body
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="80px" label-position="top">
      <!-- 基本信息 -->
      <div class="form-section">
        <div class="form-section-title">基本信息</div>
        <el-form-item label="活动名称" prop="name">
          <el-input v-model="form.name" placeholder="如：圣山塔林姓氏源流文化节" maxlength="128" show-word-limit />
        </el-form-item>
        <el-form-item label="活动类别" prop="category">
          <el-select v-model="form.category" placeholder="请选择" style="width: 100%">
            <el-option v-for="d in categories" :key="d._id" :label="d.label" :value="String(d._id)" />
          </el-select>
        </el-form-item>
        <el-form-item label="活动概览" prop="overview">
          <el-input v-model="form.overview" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="一句话简介，列表/卡片展示" />
        </el-form-item>
        <el-form-item label="活动介绍" prop="intro" class="intro-form-item">
          <wechat-editor
            v-model="form.intro"
            placeholder="请输入活动介绍，可插入图片、视频、表格，支持直接粘贴公众号文章"
            :upload-image="uploadEditorImage"
            :upload-video="uploadEditorVideo"
          />
          <span class="form-tip intro-tip">详情页正文；图片可拖拽换位、选中可缩放</span>
        </el-form-item>
      </div>

      <!-- 封面图 -->
      <div class="form-section">
        <div class="form-section-title">封面图</div>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="横版图">
              <image-uploader v-model="form.img_landscape" />
              <span class="form-tip">16:9，列表卡片用</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="竖版图">
              <image-uploader v-model="form.img_portrait" />
              <span class="form-tip">3:4，详情页顶部用</span>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 时间 -->
      <div class="form-section">
        <div class="form-section-title">活动时间</div>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="时间类型" prop="time_type">
              <el-radio-group v-model="form.time_type">
                <el-radio v-for="t in timeTypeList" :key="t.value" :label="t.value">{{ t.label }}</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="form.time_type === 1 ? '日期范围' : '活动日期'" prop="start_date">
              <template v-if="form.time_type === 1">
                <!-- key 必须区分：v-if/v-else 同组件 Vue 会复用实例，内部 popper 参照不重绑，面板会飞到页面左上角 -->
                <el-date-picker
                  key="daterange"
                  v-model="dateRange"
                  type="daterange"
                  range-separator="至"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                  @change="handleDateRangeChange"
                />
              </template>
              <el-date-picker
                key="single"
                v-else
                v-model="form.start_date"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="选择日期"
                style="width: 100%"
                @change="val => form.end_date = ''"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="开始时刻" prop="start_time">
              <el-time-picker v-model="form.start_time" placeholder="如 09:00" value-format="HH:mm" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="结束时刻" prop="end_time">
              <el-time-picker v-model="form.end_time" placeholder="如 17:00" value-format="HH:mm" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 地点 -->
      <div class="form-section">
        <div class="form-section-title">活动地点</div>
        <el-form-item label="地点名称" prop="place_name">
          <el-autocomplete
            v-model="form.place_name"
            :fetch-suggestions="searchPlace"
            :trigger-on-focus="false"
            clearable
            popper-class="place-suggestion-popper"
            placeholder="输入关键词搜索地图地点，选中自动带出省市区"
            style="width: 100%"
            @select="onPlaceSelect"
          >
            <template slot-scope="{ item }">
              <div class="place-suggestion">
                <div class="ps-title"><span class="ps-pin"></span>{{ item.raw.title }}</div>
                <div class="ps-addr">{{ item.raw.address }}</div>
              </div>
            </template>
          </el-autocomplete>
        </el-form-item>
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="省">
              <el-select v-model="form.province" placeholder="省" filterable clearable style="width: 100%" @change="onProvinceChange">
                <el-option v-for="p in provinces" :key="p.code" :label="p.name" :value="p.code" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="市">
              <el-select v-model="form.city" placeholder="市" filterable clearable style="width: 100%" @change="onCityChange">
                <el-option v-for="c in cities" :key="c.code" :label="c.name" :value="c.code" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区/县">
              <el-select v-model="form.district" placeholder="区/县" filterable clearable style="width: 100%">
                <el-option v-for="a in areas" :key="a.code" :label="a.name" :value="a.code" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="详细地址">
          <el-input v-model="form.address" placeholder="街道门牌号，选地图地点后自动填入，可改" maxlength="255" />
        </el-form-item>
      </div>

      <!-- 活动流程：按天编排时段 -->
      <div class="form-section">
        <div class="form-section-title">
          活动流程
          <span class="form-section-sub">按天编排，每天可添加多个时段</span>
        </div>

        <div v-if="!flows.length" class="flow-empty">
          还没有流程安排，点右上「添加一天」开始编排
        </div>

        <div v-for="(day, di) in flows" :key="di" class="flow-day">
          <div class="flow-day-head">
            <span class="flow-day-badge">{{ di + 1 }}</span>
            <span class="flow-day-title">第 {{ di + 1 }} 天</span>
            <el-date-picker
              v-model="day.flow_date"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="选择日期"
              size="mini"
              style="width: 150px"
            />
            <span class="flow-count">{{ day.nodes.length }} 个时段</span>
            <div class="flow-day-ops">
              <el-button type="text" size="mini" icon="el-icon-plus" @click="addNode(di)">添加时段</el-button>
              <el-button type="text" size="mini" class="text-danger-btn" @click="removeDay(di)">删除这天</el-button>
            </div>
          </div>

          <div v-if="!day.nodes.length" class="flow-node-empty">该天暂无时段，点上方「添加时段」</div>

          <div v-for="(node, ni) in day.nodes" :key="ni" class="flow-node">
            <div class="flow-node-head">
              <span class="flow-node-index">{{ ni + 1 }}</span>
              <el-time-picker
                v-model="node.start_time"
                placeholder="开始时间"
                value-format="HH:mm"
                format="HH:mm"
                size="small"
                :clearable="false"
                style="width: 130px"
              />
              <i class="flow-tilde">~</i>
              <el-time-picker
                v-model="node.end_time"
                placeholder="结束时间"
                value-format="HH:mm"
                format="HH:mm"
                size="small"
                :clearable="false"
                style="width: 130px"
              />
              <el-input
                v-model="node.title"
                size="small"
                placeholder="时段标题，如 签到集合"
                maxlength="64"
                class="flow-input-title"
              />
              <el-button
                type="text"
                size="small"
                icon="el-icon-delete"
                class="text-danger-btn flow-node-del"
                @click="removeNode(di, ni)"
              />
            </div>
            <div class="flow-node-line">
              <span class="flow-line-label">简介</span>
              <el-input
                v-model="node.description"
                size="small"
                placeholder="简短介绍，如 最东红门集合，领取活动手册"
                maxlength="255"
              />
            </div>
            <div class="flow-node-line">
              <span class="flow-line-label">补充</span>
              <el-input
                v-model="node.extra"
                size="small"
                placeholder="选填，如 讲师：雷晶教授 / 地点：景区东门"
                maxlength="255"
              />
            </div>
          </div>
        </div>

        <div class="flow-footer">
          <el-button type="text" size="small" icon="el-icon-plus" class="flow-add-day" @click="addDay">添加一天</el-button>
          <span class="form-tip">流程将随活动一并保存</span>
        </div>
      </div>
    </el-form>

    <div slot="footer" class="dialog-footer activity-footer">
      <span class="footer-tip">
        <template v-if="isEdit && form.status === 1">当前状态：报名中（已发布）</template>
        <template v-else-if="form.status === 0">当前为草稿，发布后小程序端可见</template>
      </span>
      <div>
        <el-button @click="innerVisible = false">取 消</el-button>
        <el-button :loading="submitting" @click="submit(0)">存草稿</el-button>
        <el-button type="primary" :loading="submitting" @click="submit(1)">发 布</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'
import { createActivity, updateActivity } from '@/api/activity'
import { tencentAddressKeyword, getProvince, getCity, getArea } from '@/api/public'
import uploadToQiniu from '@/util/qiniu-upload'

const emptyForm = () => ({
  id: 0,
  name: '',
  category: '',
  img_landscape: '',
  img_portrait: '',
  overview: '',
  intro: '',
  time_type: 2,
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  place_name: '',
  province: '',
  city: '',
  district: '',
  address: '',
  lat: '',
  lng: '',
  status: 0
})

// 活动流程：按天编排，nodes 为时段数组
const emptyNode = () => ({
  start_time: '',
  end_time: '',
  title: '',
  description: '',
  extra: ''
})
const emptyDay = (date = '') => ({
  flow_date: date,
  nodes: [emptyNode()]
})

export default {
  name: 'ActivityForm',
  components: { ImageUploader, WechatEditor },
  props: {
    visible: { type: Boolean, default: false },
    row: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
    timeTypeList: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: emptyForm(),
      submitting: false,
      dateRange: [], // 仅在 time_type=1 时使用
      flows: [],     // 活动流程
      provinces: [], // 省市区三级联动（走公共接口，值绑区域编码提交）
      cities: [],
      areas: [],
      rules: {
        name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
        category: [{ required: true, message: '请选择活动类别', trigger: 'change' }],
        overview: [{ required: true, message: '请填写活动概览', trigger: 'blur' }],
        time_type: [{ required: true, message: '请选择时间类型', trigger: 'change' }],
        start_date: [
          { required: true, message: '请选择活动日期', trigger: 'change' },
          { validator: this.validateEndDate, trigger: 'change' }
        ],
        start_time: [{ required: true, message: '请选择开始时刻', trigger: 'change' }],
        end_time: [{ required: true, message: '请选择结束时刻', trigger: 'change' }],
        place_name: [{ required: true, message: '请输入地点名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    isEdit() { return !!(this.row && this.row.id) }
  },
  created() {
    // 组件由 v-if 控制，每次打开都是新实例；此处回填，保证编辑时表单有值
    // （watch 对首次传入的 prop 不触发，只靠 watch 会导致编辑弹窗空白）
    this.seed()
  },
  watch: {
    visible(val) {
      if (val) this.seed()
    }
  },
  methods: {
    // 用 row（编辑时=详情接口返回）回填表单；新增则用空表单
    seed() {
      const source = this.row ? Object.assign({}, this.row) : emptyForm()
      this.form = source
      this.dateRange = source.time_type === 1 && source.start_date && source.end_date
        ? [source.start_date, source.end_date]
        : []
      // 流程来自详情接口的 flows（列表接口只回 flow_days，不带 flows）
      this.flows = source.flows && source.flows.length
        ? JSON.parse(JSON.stringify(source.flows))
        : []
      // 省市区下拉选项：拉省列表，并按已存名称逐级反查带出市/区（编辑回显）
      this.initRegion()
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    // ===== 地点搜索与省市区联动 =====
    // 地点关键词搜索：走公共接口 /address/keyword（腾讯地图关键词输入提示）
    // 富文本图片/视频：七牛直传后挂外链（正文里不落 base64）
    uploadEditorImage(file) {
      return uploadToQiniu(file, 'activity/image')
    },
    uploadEditorVideo(file) {
      return uploadToQiniu(file, 'activity/video')
    },
    searchPlace(query, cb) {
      const keyword = (query || '').trim()
      if (!keyword) { cb([]); return }
      tencentAddressKeyword({ keyword }).then(res => {
        if (res.code !== 200 || !Array.isArray(res.data)) { cb([]); return }
        // value 供输入框回显（选中后显示 title），raw 保留完整地点信息
        cb(res.data.map(item => ({ value: item.title || '', raw: item })))
      }).catch(() => cb([]))
    },
    // 选中候选地点：回填名称/经纬度/省市区编码/详细地址，按腾讯中文名反查区域编码逐级联动
    onPlaceSelect(item) {
      const p = (item && item.raw) || {}
      if (p.title) this.form.place_name = p.title
      if (p.location) {
        this.form.lat = p.location.lat
        this.form.lng = p.location.lng
      }
      this.form.address = p.address || ''
      this.form.province = ''
      this.form.city = ''
      this.form.district = ''
      this.cities = []
      this.areas = []
      const prov = this.matchRegion(this.provinces, p.province)
      if (!prov) return
      this.form.province = prov.code
      this.loadCities(prov.code, () => {
        const city = this.matchRegion(this.cities, p.city)
        if (!city) return
        this.form.city = city.code
        this.loadAreas(city.code, () => {
          const area = this.matchRegion(this.areas, p.district)
          if (area) this.form.district = area.code
        })
      })
    },
    // 区域反查：先精确匹配，再包容匹配（腾讯全称「广西壮族自治区」↔ 表里「广西」等口径差异）
    matchRegion(list, name) {
      const target = (name || '').trim()
      if (!target) return null
      let hit = list.find(i => i.name === target)
      if (!hit) {
        hit = list.find(i => i.name.indexOf(target) !== -1 || target.indexOf(i.name) !== -1)
      }
      return hit || null
    },
    // 省市区联动：值绑区域编码，编辑回显按 code 逐级带出下级
    initRegion() {
      getProvince().then(res => {
        this.provinces = res.data || []
        this.cities = []
        this.areas = []
        const p = this.provinces.find(i => i.code === this.form.province)
        if (p) {
          this.loadCities(p.code, () => {
            const c = this.cities.find(i => i.code === this.form.city)
            if (c) this.loadAreas(c.code)
          })
        }
      }).catch(() => {})
    },
    loadCities(code, done) {
      getCity(code).then(res => {
        this.cities = res.data || []
        if (done) done()
      }).catch(() => {})
    },
    loadAreas(code, done) {
      getArea(code).then(res => {
        this.areas = res.data || []
        if (done) done()
      }).catch(() => {})
    },
    onProvinceChange(code) {
      this.form.city = ''
      this.form.district = ''
      this.cities = []
      this.areas = []
      const p = this.provinces.find(i => i.code === code)
      if (p) this.loadCities(p.code)
    },
    onCityChange(code) {
      this.form.district = ''
      this.areas = []
      const c = this.cities.find(i => i.code === code)
      if (c) this.loadAreas(c.code)
    },
    handleDateRangeChange(val) {
      if (Array.isArray(val) && val.length === 2) {
        this.form.start_date = val[0]
        this.form.end_date = val[1]
      } else if (Array.isArray(val) && val.length === 0) {
        this.form.start_date = ''
        this.form.end_date = ''
      }
    },
    // ===== 活动流程 =====
    addDay() {
      // 默认日期：优先活动开始日期 + 已有一天则顺延
      let date = ''
      if (this.flows.length === 0 && this.form.start_date) {
        date = this.form.start_date
      } else if (this.flows.length) {
        const last = this.flows[this.flows.length - 1].flow_date
        if (last) date = this.shiftDate(last, 1)
      }
      this.flows.push(emptyDay(date))
    },
    removeDay(di) {
      this.$confirm('确认删除这一天的流程编排？', '删除确认', { type: 'warning' })
        .then(() => { this.flows.splice(di, 1) })
        .catch(() => {})
    },
    addNode(di) {
      const day = this.flows[di]
      const prev = day.nodes[day.nodes.length - 1]
      const node = emptyNode()
      // 顺延上一个时段的结束时间作为新时段开始，减少手工输入
      if (prev && prev.end_time) node.start_time = prev.end_time
      day.nodes.push(node)
    },
    removeNode(di, ni) {
      this.flows[di].nodes.splice(ni, 1)
    },
    shiftDate(dateStr, days) {
      const d = new Date(dateStr.replace(/-/g, '/'))
      if (isNaN(d.getTime())) return ''
      d.setDate(d.getDate() + days)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    // 时间类型=日期范围时，必须选满起止日期（后端同样强校验）
    validateEndDate(rule, value, callback) {
      if (this.form.time_type === 1 && !this.form.end_date) {
        callback(new Error('请选择结束日期'))
        return
      }
      callback()
    },
    // 提交白名单：只发后端认识的字段，不带 id / 列表态的 flow_days 等
    buildPayload(publishStatus) {
      const isRange = this.form.time_type === 1
      return {
        name: this.form.name,
        category: this.form.category,
        img_landscape: this.form.img_landscape || '',
        img_portrait: this.form.img_portrait || '',
        overview: this.form.overview,
        intro: this.form.intro || '',
        time_type: this.form.time_type,
        start_date: this.form.start_date,
        end_date: isRange ? this.form.end_date : '',
        start_time: this.form.start_time,
        end_time: this.form.end_time,
        place_name: this.form.place_name,
        province: this.form.province || '',
        city: this.form.city || '',
        district: this.form.district || '',
        address: this.form.address || '',
        // 表单没有经纬度输入，原样回传已有值，避免编辑时把库里坐标清成 0
        lat: this.form.lat == null ? '' : this.form.lat,
        lng: this.form.lng == null ? '' : this.form.lng,
        status: publishStatus,
        flows: JSON.parse(JSON.stringify(this.flows))
      }
    },
    // publishStatus: 0 存草稿 / 1 发布
    submit(publishStatus) {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const payload = this.buildPayload(publishStatus)
        const request = this.form.id
          ? updateActivity(this.form.id, payload)
          : createActivity(payload)
        request.then(res => {
          if (res.code !== 200) return
          this.$message.success(publishStatus === 1 ? '已发布' : '已存为草稿')
          this.$emit('saved')
          this.innerVisible = false
        }).finally(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>

<style scoped>
.activity-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}

/* 分段标题 */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-section-sub {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  white-space: nowrap;   /* 提示文字不换行，跟控件挤不下时宁可让溢出的部分被截/换区，也不让单字掉到下一行 */
}

/* 活动介绍（富文本）：label 顶部对齐，说明文字独立成行左对齐 */
.intro-form-item >>> .el-form-item__label {
  padding-top: 2px;
  line-height: 32px;
}
.intro-tip {
  display: block;
  margin: 6px 0 0;
}

/* 地点搜索候选：上行=定位点+名称，下行=灰色地址（老 Chromium 无 flex gap，一律用 margin） */
.place-suggestion {
  padding: 6px 0;
  line-height: 20px;
}
.ps-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.ps-pin {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: var(--primary, #8E1103);
  box-shadow: 0 0 0 3px rgba(142, 17, 3, 0.12);
}
.ps-addr {
  margin: 4px 0 0 16px;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== 活动流程 ===== */
.flow-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: #a8abb2;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  margin-bottom: 12px;
}

.flow-day {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

/* 天头部：编号徽标 + 第N天 + 日期 + 时段数 + 右侧操作 */
.flow-day-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  background: #f7f8fa;
  border-bottom: 1px solid #ebeef5;
}
.flow-day-badge {
  flex: 0 0 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 5px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
}
.flow-day-title {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}
.flow-count {
  font-size: 12px;
  color: #909399;
}
.flow-day-ops {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
}
.flow-day-ops .el-button--text {
  padding: 0 4px;
}

.flow-node-empty {
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: #a8abb2;
}

/* 时段：每个时段一个三行小块（时间/标题 → 简介 → 补充） */
.flow-node {
  padding: 10px 14px;
  border-bottom: 1px solid #f5f6f8;
}
.flow-node:last-child {
  border-bottom: none;
}

/* 第一行：序号 + 开始/结束时间 + 标题 + 删除 */
.flow-node-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.flow-node-index {
  flex: 0 0 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 50%;
  background: var(--primary-300, #f7e4e1);
  color: var(--primary);
  font-size: 12px;
  font-weight: 500;
}
.flow-tilde {
  color: #c0c4cc;
  font-style: normal;
  flex: 0 0 auto;
}
.flow-input-title {
  flex: 1;
  min-width: 0;
}
.flow-node-del {
  flex: 0 0 auto;
  padding: 0 4px;
}

/* 第二/三行：带小标签的整行输入 */
.flow-node-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-left: 28px;
}
.flow-line-label {
  flex: 0 0 28px;
  font-size: 12px;
  color: #909399;
}

.flow-footer {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}
.flow-add-day {
  font-size: 13px;
}

/* 底部：左提示 + 右按钮组 */
.activity-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.footer-tip {
  font-size: 12px;
  color: #909399;
}
</style>

<style>
/* 地点搜索下拉 popper 挂在 body 下，scoped 够不着；用 popper-class 定点覆盖，不影响其他下拉 */
.place-suggestion-popper .el-autocomplete-suggestion__wrap {
  padding: 6px 0;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(22, 4, 0, 0.12);
}
.place-suggestion-popper .el-autocomplete-suggestion__wrap li {
  padding: 4px 16px;
  line-height: 20px;
}
.place-suggestion-popper .el-autocomplete-suggestion__wrap li:hover,
.place-suggestion-popper .el-autocomplete-suggestion__wrap li.highlighted {
  background: rgba(142, 17, 3, 0.06);
}
.place-suggestion-popper .popper__arrow {
  display: none;
}
</style>